import { Matrix4, Quaternion, Vector3, Vector3Tuple } from 'three'
import { ElementLabelDescriptor } from '../ModelElementLabels/types'
import { TEXT_3D_LABEL_MARGIN_WORLD } from '../TextSprite/utils'
import { BatchedLabelTextSize } from './BatchedLabelText'
import { LABEL_BACKGROUND_DEPTH_OFFSET } from './constants'

/**
 * Rotation that maps the text plane (x right, y up) onto the given basis.
 */
export const quaternionFromBasis = (xDirection: Vector3, yDirection: Vector3): Quaternion => {
  const x = xDirection.clone().normalize()
  const y = yDirection.clone().normalize()
  const z = new Vector3().crossVectors(x, y).normalize()

  return new Quaternion().setFromRotationMatrix(new Matrix4().makeBasis(x, y, z))
}

export const getPlaneNormal = (xDirection: Vector3, yDirection: Vector3): Vector3 =>
  new Vector3().crossVectors(xDirection, yDirection).normalize()

/**
 * Corners of a rectangle centered on `center`, laid out in the plane spanned by
 * `xDirection` and `yDirection` and pushed `normalOffset` against its normal.
 */
export const makeRectanglePoints = (
  center: Vector3,
  xDirection: Vector3,
  yDirection: Vector3,
  halfWidth: number,
  halfHeight: number,
  normalOffset: number,
): Vector3Tuple[] => {
  const x = xDirection.clone().normalize().multiplyScalar(halfWidth)
  const y = yDirection.clone().normalize().multiplyScalar(halfHeight)
  const origin = center
    .clone()
    .addScaledVector(getPlaneNormal(xDirection, yDirection), -normalOffset)

  return [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([sx, sy]) => {
    const corner = origin.clone().addScaledVector(x, sx).addScaledVector(y, sy)
    return [corner.x, corner.y, corner.z] as Vector3Tuple
  })
}

/**
 * The text block a label's background wraps: the descriptor's own block size
 * where it has one, else the measured text.
 */
export const getLabelTextBlock = (
  { backgroundTextBlockSize }: ElementLabelDescriptor,
  size: BatchedLabelTextSize,
): { width: number; height: number } => backgroundTextBlockSize ?? size

/**
 * Corners of a label's background rectangle: the text block plus its margin,
 * wrapped around the visual center of the text rather than the center of its
 * line box, which sits higher.
 */
export const getLabelRectanglePoints = (
  descriptor: ElementLabelDescriptor,
  size: BatchedLabelTextSize,
): Vector3Tuple[] => {
  const { position, xDirection, yDirection, backgroundPaddingWorld, textWidthScale } = descriptor
  const padding = backgroundPaddingWorld ?? TEXT_3D_LABEL_MARGIN_WORLD
  const block = getLabelTextBlock(descriptor, size)
  return makeRectanglePoints(
    position.v.clone().addScaledVector(yDirection.clone().normalize(), size.centerOffsetY),
    xDirection,
    yDirection,
    (block.width * (textWidthScale ?? 1)) / 2 + padding,
    block.height / 2 + padding,
    LABEL_BACKGROUND_DEPTH_OFFSET,
  )
}

/**
 * Footprint of a label in the XY plane, as offsets from the label's anchor
 * position. Independent of where the label sits, so it stays valid when the
 * label is moved.
 */
export interface LabelPlanExtent {
  xMin: number
  xMax: number
  yMin: number
  yMax: number
}

export const getLabelPlanExtent = (
  descriptor: ElementLabelDescriptor,
  size: BatchedLabelTextSize,
): LabelPlanExtent => {
  const { x, y } = descriptor.position
  const points = getLabelRectanglePoints(descriptor, size)
  return {
    xMin: Math.min(...points.map(([px]) => px)) - x,
    xMax: Math.max(...points.map(([px]) => px)) - x,
    yMin: Math.min(...points.map(([, py]) => py)) - y,
    yMax: Math.max(...points.map(([, py]) => py)) - y,
  }
}
